import { Box, Grid, Paper, Typography, styled, Skeleton } from "@mui/material";
import WelcomeDashboardSection from "components/WelcomeDashboardSection";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setHeaderDetails } from "store/actions/header";
import Cookie from "utils/cookie";
import useDocumentTitle from "utils/hooks/useDocumentTitle";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(4),
  textAlign: "center",
  boxShadow: "0px 8px 40px 0px rgba(0, 0, 0, 0.2)",
  cursor: "pointer",
}));
const Welcome = () => {
  const userDetails = Cookie.getParsedUser();
  useDocumentTitle("Bienvenue sur Evocoach");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.userProfile);
  const clients = useSelector((state) => state.clients?.clients);
  const fitnessPrograms = useSelector((state) => state.programs?.programms);
  const nutritionPrograms = useSelector((state) => state.nutritions?.nutritions);

  const hasPrograms = Boolean(fitnessPrograms && fitnessPrograms.length > 0);
  const hasClients = Boolean(clients && clients.length > 0);
  const hasNutritionPrograms = Boolean(nutritionPrograms && nutritionPrograms.length > 0);
  const allTasksDone = hasPrograms && hasClients && hasNutritionPrograms;

  const isLoadingClients = useSelector((state) => state.clients?.isLoadingClients);
  const isLoadingPrograms = useSelector((state) => state.programs?.isLoadingProgramms);
  const isLoadingNutritions = useSelector((state) => state.nutritions?.isLoadingNutritins);

  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitializing(false), 150);
    return () => clearTimeout(timer);
  }, []);

  const isDataLoading = isInitializing || isLoadingClients || isLoadingPrograms || isLoadingNutritions;

  const isGuideVisible = userProfile && typeof userProfile === 'object' && Object.keys(userProfile).length > 0 && !allTasksDone && userProfile.hasSeenWelcomeVideo !== true;

  const redirectTo = (link) => {
    navigate(link);
  };
  useEffect(() => {
    const navDetail = {
      isBreadcrumns: false,
      title: "Bienvenue",
    };
    dispatch(setHeaderDetails(navDetail));
  }, []);
  return (
    <Box pb={4} sx={{ flexGrow: 1 }}>
      <Box px={{ xs: 2, md: 4 }}>
        <WelcomeDashboardSection />
      </Box>

      {isDataLoading ? (
        <Box display="flex" flexDirection="column" alignItems="center" mb={5} px={2} width="100%">
          <Skeleton variant="text" width={250} height={40} sx={{ mb: 1, mt: 1 }} />
          <Skeleton variant="text" height={24} sx={{ width: { xs: "90%", md: 600 } }} />
        </Box>
      ) : !isGuideVisible && (
        <>
          <Typography
            variant="h5"
            component="h5"
            align="center"
            mb={2}
            style={{ fontWeight: 700 }}
          >
            Content de vous revoir !
          </Typography>

          <Typography variant="body1" px={2} mb={5} align="center">
            Gagnez du temps en accédant rapidement à votre contenu et simplifiez
            votre expérience.
          </Typography>
        </>
      )}

      <Grid container spacing={3} justifyContent="center" px={{ xs: 2, md: 4 }}>
        {/* Fitness Programs List */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, pr: 1 }}>
                Derniers Prog. Fitness
              </Typography>
              <img
                src="/assets/images/evocoach_welcome_fitness.png"
                alt="Fitness"
                style={{
                  height: "80px",
                  objectFit: "contain",
                }}
              />
            </Box>

            {isDataLoading ? (
              <Box flexGrow={1}>
                {[1, 2, 3].map((_, index) => (
                  <Box key={`skel-fit-${index}`} display="flex" alignItems="center" py={1.5} borderBottom={index < 2 ? "1px solid #f0f0f0" : "none"}>
                    <Skeleton variant="rounded" width={32} height={32} sx={{ mr: 2, borderRadius: 2 }} />
                    <Box flexGrow={1}>
                      <Skeleton variant="text" width="60%" sx={{ fontSize: '1rem' }} />
                      <Skeleton variant="text" width="40%" sx={{ fontSize: '0.75rem' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : hasPrograms ? (
              <Box flexGrow={1}>
                {fitnessPrograms.slice(0, 3).map((prog, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    py={1.5}
                    borderBottom={index < Math.min(fitnessPrograms.length, 3) - 1 ? "1px solid #f0f0f0" : "none"}
                    onClick={() => navigate(`/fitness/programs/${prog._id}`)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: 2,
                      px: 1,
                      mx: -1,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box
                      bgcolor="rgba(0, 1, 252, 0.08)"
                      color="primary.main"
                      borderRadius={2}
                      p={1}
                      display="flex"
                      mr={2}
                    >
                      <span role="img" aria-label="fitness">🏋️‍♂️</span>
                    </Box>
                    <Box flexGrow={1} overflow="hidden">
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {prog.name || "Programme Sans Titre"}
                      </Typography>
                      {prog.goal && (
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {prog.goal}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={3}>
                <Typography variant="body2" color="textSecondary" align="center" mb={2}>
                  Aucun programme de fitness.
                </Typography>
                <Box
                  onClick={() => redirectTo("/fitness/programs")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      gap: 1.5,
                      color: "primary.dark",
                    }
                  }}
                >
                  Créer un programme <span>&rarr;</span>
                </Box>
              </Box>
            )}

            {hasPrograms && (
              <Box mt={2} textAlign="center">
                <Box
                  onClick={() => redirectTo("/fitness/programs")}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    transition: "all 0.2s ease",
                    "&:hover": { gap: 1 }
                  }}
                >
                  Voir tout <span>&rarr;</span>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Nutrition Programs List */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, pr: 1 }}>
                Derniers Prog. Nutrition
              </Typography>
              <img
                src="/assets/images/evocoach_welcome_nutrition.png"
                alt="Nutrition"
                style={{
                  height: "80px",
                  objectFit: "contain",
                }}
              />
            </Box>

            {isDataLoading ? (
              <Box flexGrow={1}>
                {[1, 2, 3].map((_, index) => (
                  <Box key={`skel-nut-${index}`} display="flex" alignItems="center" py={1.5} borderBottom={index < 2 ? "1px solid #f0f0f0" : "none"}>
                    <Skeleton variant="rounded" width={32} height={32} sx={{ mr: 2, borderRadius: 2 }} />
                    <Box flexGrow={1}>
                      <Skeleton variant="text" width="60%" sx={{ fontSize: '1rem' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : hasNutritionPrograms ? (
              <Box flexGrow={1}>
                {nutritionPrograms.slice(0, 3).map((prog, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    py={1.5}
                    borderBottom={index < Math.min(nutritionPrograms.length, 3) - 1 ? "1px solid #f0f0f0" : "none"}
                    onClick={() => navigate(`/nutrition/programs/${prog._id}`)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: 2,
                      px: 1,
                      mx: -1,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box
                      bgcolor="rgba(0, 1, 252, 0.08)"
                      color="primary.main"
                      borderRadius={2}
                      p={1}
                      display="flex"
                      mr={2}
                    >
                      <span role="img" aria-label="nutrition">🥗</span>
                    </Box>
                    <Box flexGrow={1} overflow="hidden">
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {prog.name || "Programme Sans Titre"}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={3}>
                <Typography variant="body2" color="textSecondary" align="center" mb={2}>
                  Aucun programme de nutrition.
                </Typography>
                <Box
                  onClick={() => redirectTo("/nutrition/programs")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      gap: 1.5,
                      color: "primary.dark",
                    }
                  }}
                >
                  Créer un programme <span>&rarr;</span>
                </Box>
              </Box>
            )}

            {hasNutritionPrograms && (
              <Box mt={2} textAlign="center">
                <Box
                  onClick={() => redirectTo("/nutrition/programs")}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    transition: "all 0.2s ease",
                    "&:hover": { gap: 1 }
                  }}
                >
                  Voir tout <span>&rarr;</span>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Clients List */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, pr: 1 }}>
                Derniers Clients
              </Typography>
              <img
                src="/assets/images/evocoach_welcome_marketing.png"
                alt="Clients"
                style={{
                  height: "80px",
                  objectFit: "contain",
                }}
              />
            </Box>

            {isDataLoading ? (
              <Box flexGrow={1}>
                {[1, 2, 3].map((_, index) => (
                  <Box key={`skel-cli-${index}`} display="flex" alignItems="center" py={1.5} borderBottom={index < 2 ? "1px solid #f0f0f0" : "none"}>
                    <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
                    <Box flexGrow={1}>
                      <Skeleton variant="text" width="60%" sx={{ fontSize: '1rem' }} />
                      <Skeleton variant="text" width="40%" sx={{ fontSize: '0.75rem' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : hasClients ? (
              <Box flexGrow={1}>
                {clients.slice(0, 3).map((client, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    py={1.5}
                    borderBottom={index < Math.min(clients.length, 3) - 1 ? "1px solid #f0f0f0" : "none"}
                  >
                    <Box
                      bgcolor="rgba(0, 1, 252, 0.08)"
                      color="primary.main"
                      borderRadius="50%"
                      width={32}
                      height={32}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      mr={2}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        {client.first_name ? client.first_name.charAt(0).toUpperCase() : "C"}
                        {client.last_name ? client.last_name.charAt(0).toUpperCase() : ""}
                      </Typography>
                    </Box>
                    <Box flexGrow={1} overflow="hidden">
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {client.first_name} {client.last_name}
                      </Typography>
                      {client.email && (
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {client.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={3}>
                <Typography variant="body2" color="textSecondary" align="center" mb={2}>
                  Aucun client pour le moment.
                </Typography>
                <Box
                  onClick={() => redirectTo("/clients")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      gap: 1.5,
                      color: "primary.dark",
                    }
                  }}
                >
                  Ajouter un client <span>&rarr;</span>
                </Box>
              </Box>
            )}

            {hasClients && (
              <Box mt={2} textAlign="center">
                <Box
                  onClick={() => redirectTo("/clients")}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    transition: "all 0.2s ease",
                    "&:hover": { gap: 1 }
                  }}
                >
                  Voir tout <span>&rarr;</span>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Tutorials Banner */}
        <Grid item xs={12} mt={1}>
          <Paper
            elevation={0}
            onClick={() => redirectTo("/tutoriels")}
            sx={{
              p: 3,
              borderRadius: 4,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              background: "linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%)",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: { xs: 2, sm: 0 },
              cursor: "pointer",
              transition: "transform 0.2s ease",
              border: "1px solid #d9e8ff",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.1)",
              }
            }}
          >
            <Box display="flex" alignItems="center" gap={3}>
              <Box
                bgcolor="white"
                width={64}
                height={64}
                flexShrink={0}
                borderRadius="50%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0px 4px 12px rgba(0, 0, 0, 0.05)"
              >
                <span role="img" aria-label="tutorials" style={{ fontSize: "2rem" }}>🎓</span>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="primary.main" mb={0.5}>
                  Besoin d'aide ou d'inspiration ?
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Découvrez nos tutoriels vidéo pour maîtriser chaque fonctionnalité de la plateforme Evocoach.
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                fontWeight: 600,
                color: "primary.main",
                bgcolor: "white",
                px: 3,
                py: 1.5,
                borderRadius: 8,
                boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                width: { xs: "100%", sm: "auto" },
                flexShrink: 0,
              }}
            >
              Voir les tutoriels <span>&rarr;</span>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
export default Welcome;
